import { Component, OnInit, inject } from '@angular/core';
import { ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from "primeng/api";
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Table } from 'primeng/table';
import { SharedService } from "../../shared/services/shared.service";
import { PortalUserService } from '../../services/admin/portal-user.service';
import { TokenStorageService } from '../../services/safeguard/token-storage.service';
import { AccountDeregQueueService } from '../../services/reports/account-dereg-queue.service'
import { IAccountDeregQueue } from '../../interfaces/account-dereg-queue';

// import { LazyLoadEvent, SelectItem } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';

interface Item {
  id: number;
  name: string;
}

@Component({
  selector: 'app-account-deregistration-queue',
  standalone: false,
  // imports: [],
  templateUrl: './account-deregistration-queue.component.html',
  styleUrl: './account-deregistration-queue.component.css',
})
export class AccountDeregistrationQueueComponent implements OnInit {

  @ViewChild('dtDeregQueue') dtDeregQueue!: Table;
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;  

  private readonly sharedService = inject(SharedService);
  private readonly activatedRoute = inject(ActivatedRoute);  
  private readonly portalUserService = inject(PortalUserService);
  private readonly tokenStorageService = inject(TokenStorageService);
  private readonly deregQueueService = inject(AccountDeregQueueService);
  private readonly confirmationService = inject(ConfirmationService);

  isSpinnerHidden: boolean = true;

  userName?: string;
  emailAddress?: string;
  isUserAdmin: boolean = false;

  selectedDateFrom!: string;
  selectedDateTo!: string;  
  selectedAccountNumber!: string;
  selectedBrandCode!: string;
  selectedBaseUrl!: string;
  selectedStatusCode!: string;
  selectedRecord: any;
  userBrands: any[] = [];
  brandCodes: any[] = [];
  statusCodes: any[] = [];  
  accountDeregQueueCols: any[] = [];
  accountDeregQueue: any[] = [];
  isBrandSelectionShown: boolean = true;
  isSearchTriggered: boolean = false;

  // VirtualScroller data
  totalRecords: number = 100; // ✅ Total dataset size
  items: (IAccountDeregQueue | null)[] = Array(this.totalRecords).fill(null); // ✅ Initialize as empty array
  loading: boolean = false;

  first = 0;
  rows = 10;

  _items: any[] =[];
  currentIndex: number = 0;
  batchSize: number = 10;
  hasMoreData: boolean = true;
  displayedColumns = ['accountDeathRowId','extAccountId','status'];

  constructor(
  ) {}

  ngOnInit() {

    // Define the grid columns
    this.accountDeregQueueCols = [
      { field: 'accountDeathRowId', header: 'Rec Id', width:'5%', display: 'table-cell' },
      // { field: 'brandCode', header: 'Brand Code', width:'5%', display: 'table-cell' },
      { field: 'brandName', header: 'Brand Name', width:'12%', display: 'table-cell' },
      { field: 'extAccountId', header: 'Account No', width:'8%', display: 'table-cell' },
      { field: 'status', header: 'Status', width:'12%', display: 'table-cell' },
      { field: 'placementDt', header: 'Placement Date', width:'9%', display: 'table-cell' },
      { field: 'gracePeriodExpireDt', header: 'Expiration Date', width:'9%', display: 'table-cell' },
      { field: 'daysRemaining', header: 'Days Remaining in Grace Period', width:'8%', display: 'table-cell', centered: true },
      { field: 'deregistrationDt', header: 'Deregistration Date', width:'9%', display: 'table-cell' },
      { field: 'restorationDt', header: 'Restoration Date', width:'9%', display: 'table-cell' }
    ] 

    // Prepopulate the Status Code dropdown
    this.statusCodes = [
      {label: '', value: null},
      {label: 'Deregistration Candidate', value: 'G'},
      {label: 'Deregistration Initiated', value: 'X'},
      {label: 'Deregistered', value: 'D'},
      {label: 'Restored by Client', value: 'R'},
      {label: 'Unenrolled by User', value: 'U'}
    ];
    this.selectedStatusCode = '';

    // Prepopulate date from and to
    this.selectedDateFrom = this.sharedService.getOneWeekAgoDate();
    this.selectedDateTo = this.sharedService.getCurrentDate();

    // Find out whether we need the entire account deregistration queue for the given brand or only expired passes
    this.activatedRoute.queryParamMap.subscribe((params) => {
      let expiredOnly: string = params.get('expiredOnly') || '';
        if (expiredOnly) {
          this.selectedStatusCode = 'D';
        }
      });

    // Initialize placeholder array for VirtualScroller
    this.items = Array(this.totalRecords).fill(null);
    
    // Figure out the list of granted brands to the current user
    this.getGrantedBrands();

    // Figure out current user's email address
    var currentUserValue = this.tokenStorageService.getCurrentUser();
    // console.log('currentUserValue', currentUserValue);
    // Determine if the user has admin privileges, so we can decide whether to show timestamps in the deregistration queue
    if (currentUserValue != null) {
      if (currentUserValue.userAttributeList) {
        var userType = currentUserValue.userAttributeList.find((attr: any) => attr.attributeCode === 'userType');
        if (userType && userType.attributeValue === 'admin') {
          this.isUserAdmin = true;
        }
      }
      // Determine the email address of the current user from the user profile, so we can use it later when the user requests generation of a file with accounts in deregistration queue
      this.userName = currentUserValue.userName;
      this.portalUserService.getUserEmailAddress(this.userName)
      .subscribe(result => {
          this.emailAddress = result; 
      });
    }    
    // console.log('isUserAdmin', this.isUserAdmin);

  }

  // When the user clicks on the Search button of the selection criteria
  onSearchClick(event: any) {
    this.accountDeregQueue = [];
    this.dtDeregQueue.reset();
    this.isSearchTriggered = true;
  }

  // Clear the selection criteria
  onClearClick(event: any) {
    this.selectedBaseUrl = '';
    this.selectedBrandCode = '';
    this.selectedStatusCode = '';
    this.selectedAccountNumber = '';
    this.selectedDateFrom = '';
    this.selectedDateTo = '';
    this.accountDeregQueue = [];
    this.dtDeregQueue.reset();
  }

  // Determine the base URL of the selected brand
  onChangeBrand(event: any) {
    this.selectedBaseUrl = '';
    if (this.userBrands) {
      const selectedBrand = this.userBrands.find(brand => brand.brandCode === event.value);
      this.selectedBaseUrl = selectedBrand ? selectedBrand.environment.baseUrl : '';  
      this.isSearchTriggered = true;
    }
  }

  // Find out the list of granted brands to the current user, so we can populate the dropdown for brands
  getGrantedBrands() {
    this.userBrands = [];
    this.brandCodes = [];
    this.isSpinnerHidden = false;
    this.brandCodes.push({label: '', value: null});
    this.portalUserService.getBillerBrands()
    .subscribe({
      next: (response) => {
          console.log('getGrantedBrands - response', response);
        if (Array.isArray(response)) {
          this.userBrands = response;
          response.forEach(brand => {
            if (brand.isActive) {
              this.brandCodes.push({label: brand.brandCode + ' - ' + brand.brandName, value: brand.brandCode});
            }
          })
          // If only one brand is granted to the user, then hide selection of brands from the criteria
          if (response.length == 1) {
            const brand0 = response[0];
            if (!brand0) return;
            this.isBrandSelectionShown = false;
            this.selectedBrandCode = brand0.brandCode;
            const selectedBrand = this.userBrands.find(brand => brand.brandCode === this.selectedBrandCode);
            this.selectedBaseUrl = selectedBrand ? selectedBrand.environment.baseUrl : '';  
            this.isSearchTriggered = true;
          } else {
            this.brandCodes.sort(this.compare);
            this.selectedBrandCode = '';
            this.isBrandSelectionShown = true;
          }
        }
        this.isSpinnerHidden = true;
      },
      error: (error) => {
        // this.alertTheUser("Unable to retrieve RTPN billers.");
        this.isSpinnerHidden = true;      },
      complete: () => {
      }
    });
  }  

  compare(a: any, b: any) {
    if ((a.brandCode || '') > (b.brandCode || '')) return 1;
    if ((b.brandCode || '') > (a.brandCode || '')) return -1;
    return 0;
  }  

  // Lazy p-table population
  loadAccountDeregQueueLazy(event: TableLazyLoadEvent) {
    if (!this.isSearchTriggered) {
      return; // ignore initial load
    }
    this.loading = true;
      // console.log('loadAccountDeregQueueLazy - event', event);

    // const page = (event.first ?? 0) / (event.rows ?? 10);
    const size = event.rows ?? this.batchSize;
    const offset = (event.first ?? 0); // + (event.rows ?? 10);
      // console.log('loadAccountDeregQueueLazy - offset', offset);

    this.getChunkOfAccountDeregistrationQueue(this.selectedBrandCode, offset, size, this.selectedBaseUrl,
      this.selectedAccountNumber, this.selectedStatusCode, this.selectedDateFrom, this.selectedDateTo
    );

    //trigger change detection
    event.forceUpdate?.();
  }


  // This converts Map<string,string> into an Array
  getAccountDataPoints(rowData: any): [string, string][] {
    var map = new Map<string, string>(
            Object.entries(rowData.accountDataPoints)
          );
    return Array.from(map.entries());
  }

  getChunkOfAccountDeregistrationQueue(brandCode: string, offset: number, limit: number, baseUrl: string,
    accountNo?: string | undefined, status?: string | undefined, 
    dateStart?: string | undefined, dateEnd?: string | undefined
  ): void {
    this.isSpinnerHidden = false;
    // var emptyList: IAccountDeregQueue[] = [];
    this.deregQueueService.findAccountsInDeregQueue(brandCode, offset, limit, baseUrl, accountNo, status, 
      dateStart ? this.sharedService.getFormattedDate(dateStart) : undefined, 
      dateEnd ? this.sharedService.getFormattedDate(this.sharedService.getNextDate(dateEnd)) : undefined
    ) 
    .subscribe({
      next: (response) => {
        this.accountDeregQueue = response.accountDeathRowList;
        // Reformate the dates to be more readable, if needed.  Admin users should see the timestamps, so skip reformatting for them
        if (!this.isUserAdmin) {
          this.accountDeregQueue.forEach((item: any) => {
            if (item.placementDt) {
              item.placementDt = this.sharedService.getFormattedDate(new Date(item.placementDt));
            }
            if (item.gracePeriodExpireDt) {
              item.gracePeriodExpireDt = this.sharedService.getFormattedDate(new Date(item.gracePeriodExpireDt));
            }
            if (item.deregistrationDt) {
              item.deregistrationDt = this.sharedService.getFormattedDate(new Date(item.deregistrationDt));
            }
            if (item.restorationDt) {
              item.restorationDt = this.sharedService.getFormattedDate(new Date(item.restorationDt));
            } 
          });
        }
        this.totalRecords = response.totalCnt;
        // console.log('totalRecords', this.totalRecords);
        this.loading = false;
        this.isSpinnerHidden = true;
      },
      error: (error) => {
        this.loading = false;
        this.isSpinnerHidden = true;
        },
      complete: () => {
      }
    });
  }

  onRowSelect(event: any) {
  }

  onFileCreationRequest(event: any) {
    if (!(this.emailAddress && this.userName)) {
        this.alertTheUser('Warning', 'Unable to figure out your email address from the user profile.  See system administrator.');
        return;
    }
    // Call the service to invoke a Web API call
    this.deregQueueService.generateFileWithAccountsInDeregQueue(this.userName, this.emailAddress, 
      this.selectedBrandCode, this.selectedStatusCode, this.selectedAccountNumber, 
      this.sharedService.getFormattedDate(this.selectedDateFrom), 
      this.sharedService.getFormattedDate(this.selectedDateTo)) 
      .subscribe({
        next: (response) => {
          this.alertTheUser('Processed', 'An email with an Account Deregistration Queue extract file attached will be sent to the address specified in your user profile.');
        },
        error: (error) => {
          this.alertTheUser('Error', error.message);
        },
        complete: () => {
        }
      });
  }  

  // lazy p-scroller population
  // lazyLoadAccountDeregQueue(event: LazyLoadEvent) {
  //   if (!this.isSearchTriggered) {
  //     return; // ignore initial load
  //   }
  //   this.loading = true;
  //   const start = event.first ?? 0;
  //   const rows = 10;//event.rows ?? 50;

  //   this.deregQueueService.findAccountsInDeregQueue('spc', start, rows, "https://qa.walletron.com/Walletron/api").subscribe(data => {
  //     for (let i = 0; i < data.length; i++) {
  //       this.items[start + i] = data[i];
  //     }
  //     this.loading = false;
  //     event.forceUpdate?.(); // ✅ Refresh VirtualScroller
  //   });
  // }


  // loadMore() {
  // console.log('currentIndex', this.currentIndex);
  // console.log('_items', this._items);
  //       if (this._items.length > 0 && this._items.findIndex(item => item.accountDeathRowId === this.currentIndex + 1) > 0) {
  //         return;
  //       }


  //   this.deregQueueService.findAccountsInDeregQueue('spc', this.currentIndex, this.batchSize, "https://qa.walletron.com/Walletron/api") 
  //   .subscribe({
  //     next: (response) => {
  //       // console.log('response', response);
  //       if (!response || response.length== 0) {
  //         this.hasMoreData = false;
  //         this.isSpinnerHidden = true;
  //         return;
  //       }
  //       // console.log('_items', this._items);
  //       if (this._items.length > 0 && this._items.findIndex(item => item.accountDeathRowId === response[0].accountDeathRowId) > 0) {
  //         return;
  //       }
  //       this._items = [...this._items, ...response];
  //       this.currentIndex = response[response.length -1].accountDeathRowId;
  //       this.isSpinnerHidden = true;
  //     },
  //     error: (error) => {
  //       this.isSpinnerHidden = true;
  //       },
  //     complete: () => {
  //     }
  //   });
  // }

  // onScroll(event: any) {
  //   if (!this.hasMoreData) return;

  //   // console.log('onScroll event', event);
  //   const start = this.viewport.getRenderedRange().start;
  //   const end = this.viewport.getRenderedRange().end;
  //   const total = this.viewport.getDataLength();

  //   // If user is near the end, load more
  //   if (end >= total - 2) {
  //     this.loadMore();
  //   }
  // }

  trackById(index: number, item: any) {
    return item.extAccountId;
  }  

  private alertTheUser(header: string, message: string) {
    this.confirmationService.confirm({
      message: message,
      header: header,
      key: 'account-dereg-queue',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Ok",
      rejectVisible: false,
      acceptButtonStyleClass: "p-button-info wal-info p-button-rounded",
      accept: () => {
         return;
       }
    });
  }  
}

